import { useEffect, useRef, useState } from "react";
import { VideoCamera, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";

const CHUNK = 4 * 1024 * 1024;

export const ChampionCameo = () => {
  const [status, setStatus] = useState("loading"); // loading | none | uploading | processing | ready | failed
  const [pct, setPct] = useState(0);
  const [cdnUrl, setCdnUrl] = useState(null);
  const [published, setPublished] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const check = async () => {
    try {
      const r = await client.get("/champion-cameo/status");
      setCdnUrl(r.data.cdn_url || null);
      setPublished(!!r.data.published);
      setStatus(r.data.status === "none" ? "none" : r.data.status);
      return r.data.status;
    } catch {
      setStatus("none");
      return "none";
    }
  };

  useEffect(() => {
    check();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "processing") return;
    pollRef.current = setInterval(async () => {
      const s = await check();
      if (s !== "processing") clearInterval(pollRef.current);
    }, 5000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 80 * 1024 * 1024) {
      toast.error("Keep the clip under 80MB — 15-30 seconds is perfect");
      return;
    }
    setStatus("uploading");
    setPct(0);
    const uploadId = `cc_${Date.now()}`;
    try {
      const chunks = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < chunks; i++) {
        const fd = new FormData();
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK));
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        await client.post("/champion-cameo/upload-chunk", fd);
        setPct(Math.round(((i + 1) / chunks) * 100));
      }
      await client.post("/champion-cameo/finalize", { upload_id: uploadId });
      setStatus("processing");
      toast.success("Got it — turning your clip into a branded reel");
    } catch (err) {
      setStatus("none");
      toast.error(err?.response?.data?.detail || "Upload failed — try again");
    }
  };

  if (status === "loading") return null;

  return (
    <div className="rounded-xl border bg-card p-5" data-testid="champion-cameo-card">
      <div className="flex items-center gap-2 mb-1">
        <VideoCamera size={18} weight="fill" className="text-[#FF5A1F]" />
        <span className="font-black">Record your shout-out</span>
      </div>
      <p className="text-sm text-muted-foreground">
        Tell other truckers what week one did for you — 15 to 30 seconds on your phone is
        plenty. We turn it into a branded reel, and Mike may feature it on freightpilotai.ai/watch.
      </p>

      {(status === "none" || status === "failed") && (
        <div className="mt-3">
          {status === "failed" && (
            <div className="text-xs text-red-500 mb-2" data-testid="champion-cameo-error">
              That one didn't process — try re-exporting as MP4 and upload again.
            </div>
          )}
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/*"
                 capture="user" className="hidden" onChange={onFile}
                 data-testid="champion-cameo-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()}
                  data-testid="champion-cameo-upload-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#FF5A1F] hover:bg-[#FF6A35] text-black px-3.5 py-2 text-sm font-bold transition-colors">
            <VideoCamera size={15} weight="bold" /> Upload your clip
          </button>
        </div>
      )}

      {status === "uploading" && (
        <div className="mt-3" data-testid="champion-cameo-uploading">
          <div className="text-xs font-mono text-muted-foreground mb-1">Uploading… {pct}%</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-[#FF5A1F] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {status === "processing" && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid="champion-cameo-processing">
          <CircleNotch size={16} className="animate-spin" />
          Building your branded reel — takes about a minute…
        </div>
      )}

      {status === "ready" && (
        <div className="mt-3" data-testid="champion-cameo-ready">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 mb-2">
            <CheckCircle size={16} weight="fill" />
            {published ? "Your reel is LIVE on freightpilotai.ai/watch" : "Your reel is ready — sent to Mike for a feature spot"}
          </div>
          {cdnUrl && (
            <video src={cdnUrl} controls playsInline preload="metadata"
                   data-testid="champion-cameo-preview"
                   className="w-40 aspect-[9/16] rounded-lg border object-cover bg-black" />
          )}
        </div>
      )}
    </div>
  );
};
